import React, { useState, ChangeEvent } from 'react';
import axios from 'axios';
import { X, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import PhotoUploadGrid from '../../components/PhotoUploadGrid';
import { useOverlayClose } from '../../components/useOverlayClose';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// ─── Shared Input Style ───────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #e2e8f0',
  borderRadius: '0.4rem', fontSize: '0.875rem', outline: 'none',
  boxSizing: 'border-box', color: '#1e293b', background: 'white',
};

const lbl: React.CSSProperties = {
  display: 'block', fontSize: '0.78rem', fontWeight: 700,
  color: '#475569', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.04em',
};

interface FieldProps { label: string; required?: boolean; children: React.ReactNode }
const Field: React.FC<FieldProps> = ({ label, required, children }) => (
  <div>
    <label style={lbl}>{label}{required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}</label>
    {children}
  </div>
);

// ─── Component ───────────────────────────────────────────────────────────────

interface BatchForm {
  vendor_name: string;
  batch_no: string;
  hsn_sac: string;
  purchase_inv_no: string;
  purchase_inv_date: string;
  expiry_date: string;
  mrp: string;
  dealer_price: string;
  selling_price: string;
  initial_quantity: string;
}

const initialForm: BatchForm = {
  vendor_name: '', batch_no: '', hsn_sac: '',
  purchase_inv_no: '', purchase_inv_date: '', expiry_date: '',
  mrp: '', dealer_price: '', selling_price: '', initial_quantity: '',
};

// Prior batch whose details pre-fill the form so the user only has to update
// the quantity. Everything stays editable in case this new lot differs.
export interface PrefillBatch {
  vendor_name?: string | null;
  batch_no?: string | null;
  hsn_sac?: string | null;
  purchase_inv_no?: string | null;
  purchase_inv_date?: string | null;
  expiry_date?: string | null;
  mrp?: number | null;
  dealer_price?: number | null;
  selling_price?: number | null;
}

const buildInitialForm = (p?: PrefillBatch | null): BatchForm => {
  if (!p) return initialForm;
  const str = (v: unknown) => (v == null ? '' : String(v));
  const dateOnly = (d?: string | null) => (d ? d.split('T')[0] : '');
  return {
    vendor_name:       str(p.vendor_name),
    batch_no:          str(p.batch_no),
    hsn_sac:           str(p.hsn_sac),
    purchase_inv_no:   str(p.purchase_inv_no),
    purchase_inv_date: dateOnly(p.purchase_inv_date),
    expiry_date:       dateOnly(p.expiry_date),
    mrp:               str(p.mrp),
    dealer_price:      str(p.dealer_price),
    selling_price:     str(p.selling_price),
    initial_quantity:  '',   // the one field the user must fill in
  };
};

interface Props {
  consumable: { id: number; name: string };
  prefill?: PrefillBatch | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AddBatchModal: React.FC<Props> = ({ consumable, prefill, onClose, onSuccess }) => {
  const [form, setForm]           = useState<BatchForm>(() => buildInitialForm(prefill));
  const [photos, setPhotos]       = useState<(File | null)[]>([null, null, null, null, null]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlayClose = useOverlayClose(onClose);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.initial_quantity.trim()) { toast.error('Quantity is required.'); return; }
    const qty = parseInt(form.initial_quantity);
    if (isNaN(qty) || qty < 1) { toast.error('Quantity must be a positive number.'); return; }

    const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
    const batchJson = JSON.stringify({
      vendor_name:       form.vendor_name.trim() || null,
      batch_no:          form.batch_no.trim() || null,
      hsn_sac:           form.hsn_sac.trim() || null,
      purchase_inv_no:   form.purchase_inv_no.trim() || null,
      purchase_inv_date: form.purchase_inv_date || null,
      expiry_date:       form.expiry_date || null,
      mrp:               form.mrp ? parseFloat(form.mrp) : null,
      dealer_price:      form.dealer_price ? parseFloat(form.dealer_price) : null,
      selling_price:     form.selling_price ? parseFloat(form.selling_price) : null,
      initial_quantity:  qty,
      current_quantity:  qty,
    });

    const formData = new FormData();
    formData.append('batch_data', batchJson);
    photos.forEach((photo, i) => { if (photo) formData.append(`photo_${i + 1}`, photo); });

    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/consumables/${consumable.id}/batches`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(`Stock batch added for "${consumable.name}"`);
      onSuccess();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to add stock batch.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      {...overlayClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: 'white', borderRadius: '1rem', boxShadow: '0 25px 50px rgba(0,0,0,0.2)', width: '100%', maxWidth: 700, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.2rem 1.5rem', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
            <div style={{ background: '#dcfce7', borderRadius: '0.5rem', padding: '0.45rem', display: 'flex' }}>
              <Package size={18} color="#15803d" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#1e293b' }}>Add Stock</h2>
              <p style={{ margin: '0.1rem 0 0', fontSize: '0.78rem', color: '#64748b' }}>
                {consumable.name}{prefill ? ' — details pre-filled, just update the quantity' : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <X size={20} />
          </button>
        </div>

        {/* ── Form ── */}
        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.9rem', overflowY: 'auto', maxHeight: '70vh' }}>

          {/* Row 1: Vendor | Batch No. | HSN/SAC */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem' }}>
            <Field label="Vendor / Dealer Name">
              <input name="vendor_name" value={form.vendor_name} onChange={handleChange} placeholder="e.g. MedSupply Co." style={inp} />
            </Field>
            <Field label="Batch Number">
              <input name="batch_no" value={form.batch_no} onChange={handleChange} placeholder="e.g. BT-2024-002" style={inp} />
            </Field>
            <Field label="HSN / SAC Code">
              <input name="hsn_sac" value={form.hsn_sac} onChange={handleChange} placeholder="e.g. 30049099" style={inp} />
            </Field>
          </div>

          {/* Row 2: Invoice No. | Invoice Date | Expiry Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.9rem' }}>
            <Field label="Purchase Invoice No.">
              <input name="purchase_inv_no" value={form.purchase_inv_no} onChange={handleChange} placeholder="e.g. INV-5467" style={inp} />
            </Field>
            <Field label="Purchase Invoice Date">
              <input name="purchase_inv_date" type="date" value={form.purchase_inv_date} onChange={handleChange} style={inp} />
            </Field>
            <Field label="Expiry Date">
              <input name="expiry_date" type="date" value={form.expiry_date} onChange={handleChange} style={inp} />
            </Field>
          </div>

          {/* Row 3: MRP | Dealer Price | Selling Price | Qty Received */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.9rem' }}>
            <Field label="MRP (₹)">
              <input name="mrp" type="number" min="0" step="0.01" value={form.mrp} onChange={handleChange} placeholder="0.00" style={inp} />
            </Field>
            <Field label="Dealer Price (₹)">
              <input name="dealer_price" type="number" min="0" step="0.01" value={form.dealer_price} onChange={handleChange} placeholder="0.00" style={inp} />
            </Field>
            <Field label="Selling Price (₹)">
              <input name="selling_price" type="number" min="0" step="0.01" value={form.selling_price} onChange={handleChange} placeholder="0.00" style={inp} />
            </Field>
            <Field label="Qty Received" required>
              <input name="initial_quantity" type="number" min="1" value={form.initial_quantity} onChange={handleChange} placeholder="e.g. 50" style={{ ...inp, fontWeight: 700, fontSize: '1rem' }} autoFocus />
            </Field>
          </div>

          {/* Row 4: Batch Photos */}
          <div>
            <p style={{ ...lbl, marginBottom: '0.6rem' }}>Batch Photos <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#94a3b8' }}>(optional)</span></p>
            <PhotoUploadGrid files={photos} onChange={setPhotos} />
          </div>

        </div>

        {/* ── Footer ── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <button onClick={onClose} style={{ padding: '0.5rem 1.1rem', background: 'none', border: '1px solid #e2e8f0', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.85rem', color: '#64748b', fontWeight: 600 }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ padding: '0.5rem 1.4rem', background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg,#059669,#047857)', color: 'white', border: 'none', borderRadius: '0.4rem', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.85rem', fontWeight: 700 }}
          >
            {isSubmitting ? 'Adding…' : '+ Add Stock'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddBatchModal;
